import React from 'react';

export default function Testimonials({ testimonials, branding }) {
  const primaryColor = branding?.primary_color || '#1e40af';
  const accentColor = branding?.secondary_color || '#AEFFE3';

  return (
    <section className="relative bg-gradient-to-b from-gray-50 to-white py-24 px-6 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-100/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-50 rounded-full border border-purple-200">
            <span className="text-sm font-semibold text-blue-900">
              Client Success Stories
            </span>
          </div>
          <h2 className="text-5xl font-bold mb-4" style={{ color: primaryColor }}>
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Real results from business owners just like you
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, idx) => (
            <div key={idx} className="relative group">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />

              {/* Glass card */}
              <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl p-8 border border-gray-200/50 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                {/* Quote icon */}
                <div className="mb-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor}40)` }}
                  >
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                </div>

                {/* Quote text */}
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>

                {/* Author */}
                <div className="pt-6 border-t border-gray-200/50">
                  <div className="flex items-center gap-3">
                    {/* Avatar with gradient */}
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor}60)` }}
                    >
                      {testimonial.author.split(' ').map(n => n[0]).join('')}
                    </div>

                    <div>
                      <div className="font-semibold text-gray-900">
                        {testimonial.author}
                      </div>
                      <div className="text-sm text-gray-600">
                        {testimonial.role}
                      </div>
                      {testimonial.company && (
                        <div className="text-xs text-gray-500">
                          {testimonial.company}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Trust badge */}
                <div className="mt-4 flex items-center gap-1 text-xs text-gray-500">
                  <svg className="w-4 h-4" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Verified Client</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Social proof bar */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-white/80 backdrop-blur-md rounded-2xl px-8 py-6 border border-gray-200/50 shadow-lg">
            <div className="flex flex-wrap items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold" style={{ color: primaryColor }}>
                  4.9/5
                </div>
                <div className="text-sm text-gray-600">Average Rating</div>
              </div>
              <div className="h-8 w-px bg-gray-300" />
              <div className="text-center">
                <div className="text-3xl font-bold" style={{ color: primaryColor }}>
                  1,200+
                </div>
                <div className="text-sm text-gray-600">Happy Clients</div>
              </div>
              <div className="h-8 w-px bg-gray-300" />
              <div className="text-center">
                <div className="text-3xl font-bold" style={{ color: primaryColor }}>
                  $50M+
                </div>
                <div className="text-sm text-gray-600">Taxes Saved</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
